import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";

import {
  type ApiCase,
  type ApiCustomer,
  ApiError,
  type CaseCustomerRole,
  type CaseStatus,
  type CaseType,
  createCase,
  searchCustomers,
  updateCase,
} from "../lib/api";
import { useToast } from "../hooks/useToast";
import { Button, Input, Modal, Select as UiSelect, Textarea } from "./ui";

const CASE_TYPE_LABEL: Record<CaseType, string> = {
  sale: "売却",
  purchase: "購入",
  rental: "賃貸",
  management: "管理",
  other: "その他",
};

const STATUS_LABEL: Record<CaseStatus, string> = {
  open: "受付中",
  negotiating: "対応中",
  mediation: "媒介契約中",
  contracting: "売買契約手続き中",
  closed: "成約",
  lost: "失注",
  on_hold: "保留",
};

const ROLE_LABEL: Record<CaseCustomerRole, string> = {
  seller: "売主",
  buyer: "買主",
  co_seller: "共同売主",
  co_buyer: "共同買主",
  co_owner: "共有者",
  heir: "相続人",
  owner: "所有者",
  other: "その他",
};

interface CaseFormModalProps {
  mode: "create" | "edit";
  caseRecord?: ApiCase;
  onClose: () => void;
  onSuccess: () => void;
}

type CaseField = "title" | "case_type" | "status" | "property_address" | "summary" | "memo";
type CaseErrors = Partial<Record<CaseField, string>>;

export function CaseFormModal({
  mode,
  caseRecord,
  onClose,
  onSuccess,
}: CaseFormModalProps): JSX.Element {
  const [form, setForm] = useState(() => initialForm(caseRecord));
  const [errors, setErrors] = useState<CaseErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const customerPicker = useCustomerPicker(mode);
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: () =>
      mode === "create"
        ? createCase(buildCreateInput(form, customerPicker.selected, customerPicker.role))
        : updateCase(requiredCase(caseRecord).id, buildUpdateInput(form)),
    onSuccess: () => {
      toast.success(mode === "create" ? "案件を作成しました" : "案件を保存しました");
      onSuccess();
      onClose();
    },
    onError: (error) => applyApiError(error, setErrors, setGeneralError),
  });

  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (mutation.isPending) return;
    const nextErrors = validate(form);
    setErrors(nextErrors);
    setGeneralError(null);
    if (Object.keys(nextErrors).length === 0) mutation.mutate();
  };
  const updateField = (field: CaseField, value: string): void => {
    setForm({ ...form, [field]: value });
    setErrors((current) => withFieldError(current, field, validateField(field, value)));
  };

  return (
    <Modal
      open
      title={mode === "create" ? "新規案件" : "案件編集"}
      onClose={onClose}
      className="max-w-2xl"
    >
      <form onSubmit={submit} noValidate>
        <ErrorBanner message={generalError} />
        <CaseFields
          form={form}
          errors={errors}
          onChange={updateField}
        />
        {mode === "create" && <CustomerPicker picker={customerPicker} />}
        <Actions pending={mutation.isPending} mode={mode} onClose={onClose} />
      </form>
    </Modal>
  );
}

function useCustomerPicker(mode: "create" | "edit") {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState<ApiCustomer | null>(null);
  const [role, setRole] = useState<CaseCustomerRole>("seller");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);
  const result = useQuery({
    queryKey: ["customers", "case-form", debouncedQuery],
    queryFn: () => searchCustomers({ q: debouncedQuery, limit: 10 }),
    enabled: mode === "create" && debouncedQuery.length > 0,
  });
  return { query, setQuery, selected, setSelected, role, setRole, result };
}

function initialForm(caseRecord: ApiCase | undefined): Record<CaseField, string> {
  return {
    title: caseRecord?.title ?? "",
    case_type: caseRecord?.case_type ?? "sale",
    status: caseRecord?.status ?? "open",
    property_address: caseRecord?.property_address ?? "",
    summary: caseRecord?.summary ?? "",
    memo: caseRecord?.memo ?? "",
  };
}

function validate(form: Record<CaseField, string>): CaseErrors {
  const titleError = validateField("title", form.title);
  return titleError === undefined ? {} : { title: titleError };
}

function validateField(field: CaseField, value: string): string | undefined {
  if (field !== "title") return undefined;
  return value.trim().length === 0 ? "タイトルは必須です" : undefined;
}

function withFieldError(
  current: CaseErrors,
  field: CaseField,
  message: string | undefined,
): CaseErrors {
  const next = { ...current };
  if (message === undefined) {
    delete next[field];
    return next;
  }
  next[field] = message;
  return next;
}

function buildCreateInput(
  form: Record<CaseField, string>,
  selected: ApiCustomer | null,
  role: CaseCustomerRole,
): Parameters<typeof createCase>[0] {
  const input: Parameters<typeof createCase>[0] = {
    title: form.title.trim(),
    case_type: form.case_type as CaseType,
    status: form.status as CaseStatus,
  };
  for (const key of ["property_address", "summary", "memo"] as const) {
    const value = form[key].trim();
    if (value.length > 0) input[key] = value;
  }
  if (selected !== null) {
    input.customer_id = selected.id;
    input.customer_role = role;
  }
  return input;
}

function buildUpdateInput(form: Record<CaseField, string>): Parameters<typeof updateCase>[1] {
  const input: Parameters<typeof updateCase>[1] = {
    title: form.title.trim(),
    case_type: form.case_type as CaseType,
    status: form.status as CaseStatus,
  };
  for (const key of ["property_address", "summary", "memo"] as const) {
    const value = form[key].trim();
    if (value.length > 0) input[key] = value;
  }
  return input;
}

function requiredCase(caseRecord: ApiCase | undefined): ApiCase {
  if (caseRecord === undefined) throw new Error("caseRecord is required");
  return caseRecord;
}

function applyApiError(
  error: unknown,
  setErrors: (errors: CaseErrors) => void,
  setGeneralError: (message: string | null) => void,
): void {
  if (!(error instanceof ApiError)) {
    setGeneralError("保存に失敗しました。");
    return;
  }
  const fieldErrors: CaseErrors = {};
  for (const detail of error.details ?? []) {
    if (isCaseField(detail.path)) fieldErrors[detail.path] = "このフィールドはエラー";
  }
  setErrors(fieldErrors);
  setGeneralError(Object.keys(fieldErrors).length === 0 ? error.message : null);
}

function isCaseField(path: string): path is CaseField {
  return ["title", "case_type", "status", "property_address", "summary", "memo"].includes(path);
}

function CaseFields({
  form,
  errors,
  onChange,
}: {
  form: Record<CaseField, string>;
  errors: CaseErrors;
  onChange: (field: CaseField, value: string) => void;
}): JSX.Element {
  return (
    <div className="mt-4 space-y-3">
      <Field
        label="タイトル"
        value={form.title}
        error={errors.title}
        required
        autoFocus
        onChange={(v) => onChange("title", v)}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="種別"
          value={form.case_type as CaseType}
          labels={CASE_TYPE_LABEL}
          error={errors.case_type}
          onChange={(v) => onChange("case_type", v)}
        />
        <Select
          label="ステータス"
          value={form.status as CaseStatus}
          labels={STATUS_LABEL}
          error={errors.status}
          onChange={(v) => onChange("status", v)}
        />
      </div>
      <Field
        label="物件住所"
        value={form.property_address}
        error={errors.property_address}
        onChange={(v) => onChange("property_address", v)}
      />
      <TextArea
        label="概要"
        value={form.summary}
        error={errors.summary}
        onChange={(v) => onChange("summary", v)}
      />
      <TextArea
        label="メモ"
        value={form.memo}
        error={errors.memo}
        onChange={(v) => onChange("memo", v)}
      />
    </div>
  );
}

function CustomerPicker({ picker }: { picker: ReturnType<typeof useCustomerPicker> }): JSX.Element {
  return (
    <div className="mt-4 rounded-lg border border-border bg-bg p-3">
      <Field
        label="同時紐付けする顧客"
        value={picker.query}
        placeholder="顧客名で検索"
        onChange={(v) => {
          picker.setQuery(v);
          picker.setSelected(null);
        }}
      />
      <CustomerCandidates picker={picker} />
      <Select
        label="顧客ロール"
        value={picker.role}
        labels={ROLE_LABEL}
        onChange={(v) => picker.setRole(v as CaseCustomerRole)}
      />
    </div>
  );
}

function CustomerCandidates({ picker }: { picker: ReturnType<typeof useCustomerPicker> }): JSX.Element | null {
  if (picker.selected !== null) {
    return (
      <div className="my-2 flex items-center justify-between rounded-md bg-primary-bg px-3 py-2">
        <p className="text-xs text-primary">選択中: {picker.selected.name}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => picker.setSelected(null)}
        >
          解除
        </Button>
      </div>
    );
  }
  const candidates = picker.result.data ?? [];
  if (picker.query.trim().length === 0) return null;
  if (picker.result.isFetching) {
    return <p className="mt-2 text-xs text-text-tertiary">顧客を検索中です...</p>;
  }
  if (candidates.length === 0) {
    return <p className="mt-2 text-xs text-text-tertiary">一致する顧客がありません</p>;
  }
  return (
    <ul className="my-2 max-h-40 overflow-y-auto rounded-lg border border-border bg-surface">
      {candidates.map((customer) => (
        <li key={customer.id}>
          <button
            type="button"
            onClick={() => {
              picker.setSelected(customer);
              picker.setQuery(customer.name);
            }}
            className="block w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-primary-bg"
          >
            {customer.name}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  required = false,
  placeholder,
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  required?: boolean;
  placeholder?: string | undefined;
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <label className="block text-xs font-medium text-text-secondary">
      {label}
      <Input
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        error={error}
        className="mt-1"
      />
    </label>
  );
}

function Select<T extends string>({
  label,
  value,
  labels,
  onChange,
  error,
}: {
  label: string;
  value: T;
  labels: Record<T, string>;
  onChange: (value: T) => void;
  error?: string | undefined;
}): JSX.Element {
  return (
    <label className="block text-xs font-medium text-text-secondary">
      {label}
      <UiSelect
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        error={error}
        className="mt-1"
      >
        {(Object.entries(labels) as Array<[string, string]>).map(
          ([optionValue, optionLabel]) => (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          ),
        )}
      </UiSelect>
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
}): JSX.Element {
  return (
    <label className="block text-xs font-medium text-text-secondary">
      {label}
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        error={error}
        className="mt-1 resize-y"
      />
    </label>
  );
}

function ErrorBanner({ message }: { message: string | null }): JSX.Element | null {
  return message === null ? null : <p role="alert" className="mt-3 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">{message}</p>;
}

function Actions({ pending, mode, onClose }: { pending: boolean; mode: "create" | "edit"; onClose: () => void }): JSX.Element {
  return (
    <div className="mt-5 flex justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>キャンセル</Button>
      <Button type="submit" variant="primary" loading={pending}>{pending ? "保存中..." : mode === "create" ? "作成" : "保存"}</Button>
    </div>
  );
}
